import {Clickable, HStack, Text, VStack, getSize, Box} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {useCallback, useState} from 'react';

import {append, isEmpty, without} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormErrorContext, SetFormErrorCallback} from '../../Form/FormErrorContext';
import {WizardStepConfig} from '../WizardStepConfig';
import {OffscreenContent} from './OffscreenContent';
import {WizardStepBadge} from './WizardStepBadge';

interface WizardProps extends WizardStepConfig, TestIdProps {
  isCurrent: boolean;
  /**
   * Whether this step currently has keyboard focus (used for roving tabindex pattern).
   * Platform API addition for accessibility improvements - manages focus state in wizard navigation.
   */
  isFocused?: boolean;
  position: number;
  onClick: (position: number) => void;
  borderPosition?: 'left' | 'top';
  isBold?: boolean;
}

export function WizardStep(props: WizardProps) {
  const theme = useTheme();

  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const formErrorContextValue = useCallback<SetFormErrorCallback>(
    (name, error) => setInvalidFields(isNilOrEmpty(error) ? without([name]) : append(name)),
    [setInvalidFields]
  );
  return (
    <>
      <Clickable
        isDisabled={props.isDisabled}
        onClick={() => props.onClick(props.position)}
        data-testid={suffixTestId(`wizardStep-[${props.position}]`, props)}
      >
        <div
          role="tab"
          id={`wizard-tab-${props.position}`}
          aria-controls={props.isCurrent ? `wizard-tabpanel-${props.position}` : undefined}
          tabIndex={props.isDisabled ? -1 : props.isFocused || props.isCurrent ? 0 : -1}
          data-wizard-tab-index={props.position}
          aria-current={props.isCurrent ? 'step' : undefined}
          aria-selected={props.isCurrent ? 'true' : 'false'}
          aria-disabled={props.isDisabled ? 'true' : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              event.stopPropagation();
              if (!props.isDisabled) {
                props.onClick(props.position);
              }
            }
          }}
          css={css`
            width: 100%;
            overflow: hidden;
            height: ${getSize(18)};
            padding: ${getSize(4)} ${getSize(6)};
            border-${props.borderPosition ?? 'left'}: ${getSize(1)} solid;
            border-color: ${
              props.isCurrent ? theme.colors.general.accent : theme.colors.general.transparent
            };
            background-color: ${
              props.isCurrent ? theme.colors.general.white : theme.colors.general.transparent
            };
            &:hover {
              background-color: ${
                props.isCurrent
                  ? theme.colors.palettes.white
                  : theme.colors.palettes.neutral[30][100]
              };
            }
            &:focus-visible {
              outline: ${getSize(1)} solid transparent; /* reserve space for high-contrast modes */
              border-bottom: ${getSize(1)} solid ${theme.colors.general.accent};
            }
          `}
        >
          <HStack spacing={2}>
            <Box flexShrink={0}>
              <WizardStepBadge
                position={props.position}
                isCurrent={props.isCurrent}
                severity={isEmpty(invalidFields) ? props.severity : 'danger'}
                data-testid={props['data-testid']}
              />
            </Box>
            <VStack spacing={1} grow={1} minWidth={0}>
              <Text
                size="small"
                color={props.isDisabled ? 'tertiary' : 'primary'}
                role="heading"
                alternative={props.isBold}
              >
                {props.title}
              </Text>
              <Box width="100%" minWidth={0}>
                <Text size="xSmall" color="tertiary" noWrap>
                  {props.subtitle}
                </Text>
              </Box>
            </VStack>
          </HStack>
        </div>
      </Clickable>
      <FormErrorContext.Provider value={formErrorContextValue}>
        <OffscreenContent children={props.content} />
      </FormErrorContext.Provider>
    </>
  );
}
